/**
 * ExportMissingFieldsDialog
 *
 * Story 358: Dialog for exporting products with missing fields
 *
 * Features:
 * - Filter selection (category, severity, price tier)
 * - Basic export (instant) vs Research export (background)
 * - Cost estimation for research export
 * - Progress tracking for research jobs
 *
 * Date: 2026-01-22
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Download,
  Search,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import {
  getExportPreview,
  exportMissingFields,
  startResearchExport,
  getResearchJobStatus,
  cancelResearchJob,
  getResearchDownloadUrl,
  ExportFilters,
  ExportPreview,
  ResearchJobStatus,
} from '@/services/enrichmentReviewService';

interface ExportMissingFieldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
}

export function ExportMissingFieldsDialog({
  open,
  onOpenChange,
  siteId,
}: ExportMissingFieldsDialogProps) {
  const { toast } = useToast();

  // UI State
  const [exportType, setExportType] = useState<'basic' | 'research'>('basic');
  const [filters, setFilters] = useState<ExportFilters>({
    categories: [],
    severity: null,
    priceTier: null,
  });

  // Data State
  const [categories, setCategories] = useState<string[]>([]);
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [researchJobId, setResearchJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ResearchJobStatus | null>(null);

  // Load categories on open
  useEffect(() => {
    if (open && siteId) {
      loadCategories();
      loadPreview();
    }
  }, [open, siteId]);

  // Load preview when filters change
  useEffect(() => {
    if (open && siteId) {
      loadPreview();
    }
  }, [filters.severity, filters.priceTier]);

  // Poll job status when research is running
  useEffect(() => {
    if (!researchJobId) return;

    const pollStatus = async () => {
      try {
        const status = await getResearchJobStatus(researchJobId);
        setJobStatus(status);

        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          // Stop polling
          return;
        }
      } catch (err) {
        console.error('Failed to poll job status:', err);
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 3000);

    return () => clearInterval(interval);
  }, [researchJobId]);

  const loadCategories = async () => {
    try {
      const response = await authFetch(`/api/enrichment-opportunities/categories?site_id=${siteId}`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const data = await getExportPreview(siteId, filters);
      setPreview(data);
    } catch (err) {
      console.error('Failed to load preview:', err);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleBasicExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportMissingFields(siteId, filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `missing-fields-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export complete',
        description: 'CSV file has been downloaded.',
      });

      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleResearchExport = async () => {
    setIsExporting(true);
    try {
      const job = await startResearchExport(siteId, filters);
      setResearchJobId(job.jobId);
      setJobStatus({
        jobId: job.jobId,
        status: 'pending',
        progress: 0,
        totalProducts: job.totalProducts,
        processedProducts: 0,
        valuesFound: 0,
        notAvailable: 0,
        failed: 0,
        estimatedCost: job.estimatedCost,
        actualCost: 0,
        downloadUrl: null,
        error: null,
        startedAt: null,
        completedAt: null,
      });

      toast({
        title: 'Research started',
        description: 'You can monitor progress in this dialog.',
      });
    } catch (err) {
      toast({
        title: 'Failed to start research',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancelResearch = async () => {
    if (!researchJobId) return;

    try {
      await cancelResearchJob(researchJobId);
      toast({
        title: 'Research cancelled',
        description: 'The research job has been cancelled.',
      });
    } catch (err) {
      toast({
        title: 'Failed to cancel',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadResult = () => {
    if (!researchJobId) return;

    const url = getResearchDownloadUrl(researchJobId);
    window.open(url, '_blank');
  };

  const handleClose = () => {
    setResearchJobId(null);
    setJobStatus(null);
    onOpenChange(false);
  };

  const isResearching = jobStatus && !['completed', 'failed', 'cancelled'].includes(jobStatus.status);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Missing Fields</DialogTitle>
          <DialogDescription>
            Export products with missing attribute values for review or correction.
          </DialogDescription>
        </DialogHeader>

        {/* Research Progress View */}
        {researchJobId && jobStatus && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              {jobStatus.status === 'completed' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : jobStatus.status === 'failed' ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : jobStatus.status === 'cancelled' ? (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              )}
              <span className="font-medium capitalize">
                {jobStatus.status === 'generating_csv' ? 'Generating CSV' : jobStatus.status}
              </span>
            </div>

            <Progress value={jobStatus.progress} className="h-2" />

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-muted-foreground">Processed</p>
                <p className="text-lg font-semibold">
                  {jobStatus.processedProducts} / {jobStatus.totalProducts}
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <p className="text-muted-foreground">Values Found</p>
                <p className="text-lg font-semibold text-green-600">{jobStatus.valuesFound}</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <p className="text-muted-foreground">Not Available</p>
                <p className="text-lg font-semibold text-amber-600">{jobStatus.notAvailable}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <p className="text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold text-red-600">{jobStatus.failed}</p>
              </div>
            </div>

            {jobStatus.error && (
              <Alert variant="destructive">
                <AlertDescription>{jobStatus.error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              {isResearching ? (
                <Button variant="destructive" onClick={handleCancelResearch}>
                  Cancel Research
                </Button>
              ) : jobStatus.status === 'completed' ? (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                  <Button onClick={handleDownloadResult}>
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </Button>
                </>
              ) : (
                <Button onClick={handleClose}>Close</Button>
              )}
            </DialogFooter>
          </div>
        )}

        {/* Initial Export Configuration */}
        {!researchJobId && (
          <>
            <div className="space-y-4 py-4">
              {/* Filters */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Filters</Label>

                <Select
                  value={filters.severity || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, severity: v === 'all' ? null : v as ExportFilters['severity'] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Severities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Export Type */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Export Type</Label>
                <RadioGroup
                  value={exportType}
                  onValueChange={(v) => setExportType(v as 'basic' | 'research')}
                >
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="basic" id="basic" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="basic" className="font-medium cursor-pointer">
                        Basic Export
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Instant download with existing data only
                      </p>
                    </div>
                  </div>

                  {/* AI Research export - coming soon */}
                  <div className="flex items-start space-x-3 p-3 border rounded-lg opacity-50 cursor-not-allowed">
                    <RadioGroupItem value="research" id="research" className="mt-1" disabled />
                    <div className="flex-1">
                      <Label htmlFor="research" className="font-medium text-muted-foreground">
                        Export with AI Research
                        <span className="ml-2 text-xs text-muted-foreground">(Coming soon)</span>
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Research missing values using AI before export
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Preview Stats */}
              {isLoadingPreview ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading preview...
                </div>
              ) : preview && (
                <div className="p-3 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Products with missing fields:</span>
                    <span className="font-medium">{preview.totalProducts}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total missing fields:</span>
                    <span className="font-medium">{preview.totalFields}</span>
                  </div>
                  {preview.topMissingFields.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-1">Top missing fields:</p>
                      <div className="flex flex-wrap gap-1">
                        {preview.topMissingFields.slice(0, 5).map((field) => (
                          <Badge key={field.name} variant="secondary" className="text-xs">
                            {field.name} ({field.count})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={exportType === 'basic' ? handleBasicExport : handleResearchExport}
                disabled={isExporting || !preview || preview.totalProducts === 0}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : exportType === 'basic' ? (
                  <Download className="h-4 w-4 mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                {exportType === 'basic' ? 'Export CSV' : 'Start Research'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
